import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getLeaveRequests } from "../../apis/leaveRequest";
import RequestLeaveModal from "./RequestLeaveModal";
import Modal from "../../components/Modal";
import CommonPagination from "../../components/CommanPagination";
import { LanguageContext } from "../../providers/LanguageProvider";

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800",
};

const LeaveRecordsList = () => {
  const navigate = useNavigate();
  const { translate: t } = useContext(LanguageContext);
  const [leaveRecords, setLeaveRecords] = useState<any[]>([]);
  const [meta, setMeta] = useState<{
    currentPage: number;
    perPage: number;
    total: number;
  }>({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [isRequestModalOpen, setRequestModalOpen] = useState(false);

  const fetchLeaveRecords = (params: any = { page: 1, per_page: 10 }) => {
    setLoading(true);
    getLeaveRequests(params)
      .then((res: any) => {
        const data = res.data?.data ?? res.data ?? [];
        const pagination = res.data?.meta ?? res.meta ?? {};
        setLeaveRecords(Array.isArray(data) ? data : []);
        setMeta({
          currentPage: pagination.currentPage ?? pagination.current_page ?? 1,
          perPage: pagination.perPage ?? pagination.per_page ?? 10,
          total: pagination.total ?? 0,
        });
      })
      .catch(() => {
        setLeaveRecords([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLeaveRecords({ page: 1, per_page: 10 });
  }, []);

  const onPaginationChange = (page: number, pageSize?: number) => {
    fetchLeaveRecords({ page, per_page: pageSize ?? 10 });
  };

  const handleRequestSuccess = () => {
    fetchLeaveRecords({ page: 1, per_page: meta.perPage });
    setRequestModalOpen(false);
  };

  const getDateRange = (record: any) => {
    const start = record.startDate ?? record.start_date ?? "";
    const end = record.endDate ?? record.end_date ?? "";
    if (!start) return "-";
    if (start === end) return start;
    return `${start} – ${end}`;
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate("/duty-roster")}
            className="text-sm font-medium text-primary hover:text-hover mb-2"
          >
            ← Back to Duty Roster
          </button>
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Leave Records
          </h1>
          <p className="mt-1 text-sm text-gray-700">
            View and request leave records.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setRequestModalOpen(true)}
          className="shrink-0 px-3 py-2 text-sm font-semibold text-center text-white rounded-md shadow-sm bg-primary hover:bg-hover"
        >
          Request Leave
        </button>
      </div>

      <div className="mt-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                Staff
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Date Range
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Reason
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Status
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Requested At
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={5} className="py-8 text-center text-sm text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : leaveRecords.length > 0 ? (
              leaveRecords.map((record) => (
                <tr key={record.id}>
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                    {record.clinicUser?.name ?? "-"}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {getDateRange(record)}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500 max-w-xs truncate">
                    {record.reason || "-"}
                  </td>
                  <td className="px-3 py-4">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                        STATUS_COLORS[record.status?.toLowerCase()] ?? "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {record.status ?? "-"}
                    </span>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {record.createdAt
                      ? new Date(record.createdAt).toLocaleString()
                      : "-"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="py-8 text-center text-sm text-gray-500">
                  No leave records yet. Request a leave to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <CommonPagination meta={meta} onChange={onPaginationChange} />
      </div>

      {isRequestModalOpen && (
        <Modal
          title="Request Leave"
          onClose={() => setRequestModalOpen(false)}
          modalContent={
            <RequestLeaveModal
              onClose={() => setRequestModalOpen(false)}
              onSuccess={handleRequestSuccess}
            />
          }
        />
      )}
    </div>
  );
};

export default LeaveRecordsList;
